window.VideoButton ??= class VideoButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
  }

  connectedCallback() {
    this.style.cursor = 'pointer';

    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');

    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
  }

  // Keyboard activation (Enter / Space), matching a native button.
  handleKeydown(event) {
    if (this.getAttribute('role') !== 'button') return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  handleClick(event) {
    const videoUrl = this.dataset.url;
    if (videoUrl) {
      createIframeWithWrapper(videoUrl);
    }
  }
};

// Define the custom element
if (!customElements.get('video-button')) {
  customElements.define('video-button', window.VideoButton);
}

// `var` (not `let`) so a second copy of this file can redeclare it; both copies run at load, before any modal opens.
var releaseVideoModalFocus = null;

/**
 * Opens the video modal: the player plus a close button that stays above it. Scroll is locked in
 * place, focus moves into the modal, and Escape or a click on the dimmed backdrop closes it.
 * Security: the URL comes from theme settings/metafields via data-url; it is only used as a media or
 * iframe src (never injected as HTML).
 * @param {string} videoSrc - MP4/MOV/MKV/WebM file URL, or a YouTube/Vimeo embed URL.
 */
function createIframeWithWrapper(videoSrc) {
  if (document.getElementById('modal-video-container')) return;

  const container = createContainer();
  const iframe = createIframe(videoSrc);
  const closeButton = createCloseModalButton();

  container.appendChild(closeButton);
  container.appendChild(iframe);
  document.body.appendChild(container);

  container.addEventListener('click', (event) => {
    if (event.target === container) closeVideoModal();
  });

  lockBodyScroll();

  // Move focus into the modal for accessibility.
  releaseVideoModalFocus = manageDialogFocus(container, { onEscape: closeVideoModal });
}

/** Closes the video modal and returns focus to the button that opened it. */
function closeVideoModal() {
  const modal = document.getElementById('modal-video-container');
  if (!modal) return;

  modal.remove();
  unlockBodyScroll();
  releaseVideoModalFocus?.();
  releaseVideoModalFocus = null;
}

function createContainer() {
  const container = document.createElement('div');
  container.id = 'modal-video-container';
  container.classList.add('active');
  container.setAttribute('role', 'dialog');
  container.setAttribute('aria-modal', 'true');

  container.setAttribute('aria-label', window.utilsString?.general?.videoPlayer || 'Video player');
  return container;
}

function createIframe(videoSrc) {
  const cleanUrl = videoSrc.split('?')[0];
  if (
    cleanUrl.endsWith('.mp4') ||
    cleanUrl.endsWith('.mov') ||
    cleanUrl.endsWith('.mkv') ||
    cleanUrl.endsWith('.webm')
  ) {
    const video = document.createElement('video');
    video.src = `${videoSrc}`;
    video.setAttribute('controls', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '0');
    // Loops forever; the element is removed (which stops playback) when the modal closes.
    video.loop = true;
    return video;
  } else {
    const youtubeId = cleanUrl.match(/youtube(?:-nocookie)?\.com\/embed\/([\w-]+)/)?.[1];
    const playlistParam = youtubeId ? `&playlist=${youtubeId}` : '';

    const iframe = document.createElement('iframe');
    iframe.src = `${videoSrc}?rel=0&autoplay=1&mute=0&loop=1${playlistParam}`;

    iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');

    // Return the iframe element.
    return iframe;
  }
}

function createCloseModalButton() {
  const button = document.createElement('button');
  button.type = 'button';
  button.id = 'closeModalVideo';
  button.setAttribute('aria-label', window.utilsString?.general?.close || 'Close');
  button.innerHTML =
    '<svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M5.46967 5.46967c.29289-.29289.76777-.29289 1.06066 0L12 10.9393l5.4697-5.46963c.2929-.29289.7677-.29289 1.0606 0 .2929.29289.2929.76777 0 1.06066L13.0607 12l5.4696 5.4697c.2929.2929.2929.7677 0 1.0606-.2929.2929-.7677.2929-1.0606 0L12 13.0607l-5.46967 5.4696c-.29289.2929-.76777.2929-1.06066 0s-.29289-.7677 0-1.0606L10.9393 12 5.46967 6.53033c-.29289-.29289-.29289-.76777 0-1.06066Z" fill="currentColor"/></svg>';

  button.addEventListener('click', closeVideoModal);

  return button;
}
